import { Controller, type FieldError, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import {
  type BookingFormValues,
  type ContactPersonConfig,
  type ContactPersonField,
  type PhoneContactPersonFieldConfig,
  type TextContactPersonFieldConfig,
} from 'pages/kyrgyzstan/booking/model'

import { EMAIL_REGEX } from 'shared/constants/regex'
import { validatePhone } from 'shared/lib/validatePhone'
import { InputWithLabel } from 'shared/ui/input-label'
import { Paper } from 'shared/ui/paper'
import { PhoneInputWhitLabel } from 'shared/ui/phone-input-label'

import s from './styles.module.scss'

const labelKeyByField: Record<ContactPersonField, string> = {
  lastName: 'kyrgyzstan_block.guest_data.last_name_placeholder',
  firstName: 'kyrgyzstan_block.guest_data.first_name_placeholder',
  middleName: 'kyrgyzstan_block.guest_data.middle_name_placeholder',
  email: 'kyrgyzstan_block.guest_data.email_placeholder',
  phone: 'kyrgyzstan_block.guest_data.phone_placeholder',
}

interface BookingContactPersonCardProps {
  config: ContactPersonConfig
  isDisabled?: boolean
}

export const BookingContactPersonCard = ({
  config,
  isDisabled = false,
}: BookingContactPersonCardProps) => {
  const { t } = useTranslation()
  const {
    control,
    formState: { errors },
  } = useFormContext<BookingFormValues>()
  const requiredMessage = t('kyrgyzstan_block.booking.errors.missing_data')
  const invalidFormatMessage = t('edit_profile.error_email')

  const getRequiredRule = (isRequired: boolean) =>
    isRequired ? requiredMessage : false

  const renderPhoneField = (fieldConfig: PhoneContactPersonFieldConfig) => (
    <Controller
      key={fieldConfig.name}
      name="phone"
      control={control}
      rules={{
        required: getRequiredRule(fieldConfig.required),
        validate: (value) => validatePhone(value) || invalidFormatMessage,
      }}
      render={({ field }) => (
        <PhoneInputWhitLabel
          value={field.value}
          onChange={field.onChange}
          label={t(labelKeyByField[fieldConfig.name])}
          variant="secondary"
          disabled={isDisabled}
          error={errors.phone}
        />
      )}
    />
  )

  const renderTextField = (fieldConfig: TextContactPersonFieldConfig) => (
    <Controller
      key={fieldConfig.name}
      name={fieldConfig.name}
      control={control}
      rules={{
        required: getRequiredRule(fieldConfig.required),
        ...(fieldConfig.validation === 'email'
          ? {
              pattern: {
                value: EMAIL_REGEX,
                message: invalidFormatMessage,
              },
            }
          : {}),
      }}
      render={({ field }) => (
        <InputWithLabel
          ref={field.ref}
          label={t(labelKeyByField[fieldConfig.name])}
          value={field.value ?? ''}
          type={fieldConfig.inputType}
          onChange={(event) => field.onChange(event.currentTarget.value)}
          variant="secondary"
          disabled={isDisabled}
          error={errors[fieldConfig.name] as FieldError}
        />
      )}
    />
  )

  return (
    <Paper
      padding={[12]}
      radius="16px"
      className={s.section}
    >
      <h2 className={clsx('bold-16', s.sectionTitle)}>
        {t('kyrgyzstan_block.guest_data.contacts')}
      </h2>

      <div className={s.fields}>
        {config.fields.map((fieldConfig) => {
          switch (fieldConfig.type) {
            case 'phone':
              return renderPhoneField(fieldConfig)
            case 'text':
              return renderTextField(fieldConfig)
            default:
              return null
          }
        })}
      </div>
    </Paper>
  )
}
